import { MetaRepository } from '../repositories/MetaRepository'
import { recordBreadcrumb, captureOfflineError } from '../telemetry'

/**
 * Cache offline de los ROLES DE FIRMA ADICIONAL (tabla `meta` de SQLite, via MetaRepository).
 *
 * El diálogo de firma adicional (components/modals/Signature.vue) tiene un combobox de "Rol"
 * que online se llena con `OccurrencesService.signaturesRoles` (POST roles/). Sin red ese
 * catálogo llegaba VACÍO y la validación de cierre (`SignatureConfirmSaveAndClose`) bloqueaba
 * el finalizar offline (ISS-843).
 *
 * HALLAZGO (verificado contra backend real): el endpoint devuelve `[{name}]` SIN `id`
 * (ej. `{name:'superv'}`). Por eso NO se filtra por id: persistimos la LISTA CRUDA tal cual
 * la devuelve el server, serializada como JSON en `meta.value`, keyed por el set de
 * `signature_roles` del assignment (distintos assignments → distintas listas, no se pisan).
 * La lectura devuelve la lista COMPLETA cacheada, SIN filtrar (igual que el flujo online).
 */

const KEY_PREFIX = 'sigroles:'

/**
 * Key estable para una lista de roles. Se deriva del set de `signature_roles` del payload
 * (ordenados + serializados) para soportar distintos assignments. Si el payload no trae
 * `signature_roles` usable (la acción a veces se llama sin ellos), se usa el fallback estable
 * `sigroles:_default` para no perder el cache y que online/offline coincidan en la misma key.
 */
const cacheKey = payload => {
  const ids = payload && Array.isArray(payload.signature_roles)
    ? payload.signature_roles
    : null
  if (!ids || !ids.length) return `${KEY_PREFIX}_default`
  const sorted = ids.map(String).sort()
  return `${KEY_PREFIX}${sorted.join(',')}`
}

/**
 * Guarda en `meta` la lista CRUDA de roles devuelta por el endpoint (JSON), keyed por el set
 * de `signature_roles` del payload. Idempotente (put upserta) y best-effort: no lanza ni rompe
 * el flujo online si `rows` viene vacío/no-array o si SQLite falla.
 * @param {object} payload el mismo payload pasado al endpoint (con signature_roles)
 * @param {Array<object>} rows lista de roles devuelta por el server (tal cual)
 * @returns {Promise<number>} cuántas filas se cachearon
 */
export const cacheSignatureRoles = async (payload, rows) => {
  const value = Array.isArray(rows) ? rows : []
  try {
    await MetaRepository.put(cacheKey(payload), JSON.stringify(value))
    recordBreadcrumb('offline.signature-roles', `${value.length} roles cacheados`)
    return value.length
  } catch (error) {
    captureOfflineError(error, { phase: 'cacheSignatureRoles' })
    return 0
  }
}

/**
 * Lee del cache la lista COMPLETA de roles de firma para el payload dado (misma key derivada
 * de `signature_roles`). Devuelve la lista tal cual se cacheó, SIN filtrar. `[]` si no hay nada.
 * @param {object} payload el mismo payload que se pasaría al endpoint
 * @returns {Promise<Array<object>>}
 */
export const getCachedSignatureRoles = async payload => {
  try {
    const raw = await MetaRepository.get(cacheKey(payload))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    captureOfflineError(error, { phase: 'getCachedSignatureRoles' })
    return []
  }
}
